import { getFirebaseEnvironment } from "@buildwithflux/constants";
import { set } from "lodash";
export function hasBrowserWindowAccess() {
    try {
        return typeof window !== "undefined";
    }
    catch (err) {
        return false;
    }
}
export function hasDocumentAccess() {
    try {
        return typeof document !== "undefined";
    }
    catch (err) {
        return false;
    }
}
export function hasNavigatorAccess() {
    try {
        return typeof navigator !== "undefined";
    }
    catch (err) {
        return false;
    }
}
export function hasSelfAccess() {
    try {
        // eslint-disable-next-line no-restricted-globals
        return typeof self !== "undefined";
    }
    catch (err) {
        return false;
    }
}
/**
 * Whether the top level 'process' variable is accessible
 *
 * Note that this returns false in the normal web context: process is a node-specific global. However, the bundling
 * makes it appear as if specific accesses to process.env.SOME_ENV_VAR are succeeding: in reality, these are being
 * rewritten by the bundler to refer to a static value.
 */
export function hasProcessAccess() {
    try {
        return typeof process !== "undefined";
    }
    catch (err) {
        return false;
    }
}
export function hasGlobalAccess() {
    try {
        return typeof global !== "undefined";
    }
    catch (err) {
        return false;
    }
}
export function isNode() {
    try {
        return !hasGlobalAccess() && "[object global]" === Object.prototype.toString.call(global);
    }
    catch (err) {
        return false;
    }
}
export function isWebWorker() {
    try {
        // eslint-disable-next-line no-restricted-globals -- self
        return !isNode() && hasSelfAccess() && globalThis === self;
    }
    catch (err) {
        return false;
    }
}
export function isBrowser() {
    return !isNode() && !isWebWorker() && hasNavigatorAccess() && hasDocumentAccess();
}
/**
 * Please note this will only return true if a user is in local environment.
 * If running in a test environment, this will NOT be true!
 *
 * @returns true if this is running in local environment (i.e. localhost)
 */
export function isDevEnv() {
    if (process.env.NODE_ENV !== "development") {
        return false;
    }
    if (hasBrowserWindowAccess()) {
        // NOTE: window.navigator.userAgent is set to "fluxsandbox" in VisualTestHelper.ts -> `getVisualTestHelper`
        return window.navigator.userAgent !== "fluxsandbox";
    }
    return true;
}
export function areWeTestingWithJest() {
    return hasProcessAccess() && process?.env?.JEST_WORKER_ID !== undefined;
}
export function areWeInStorybook() {
    return hasProcessAccess() && !!process.env.STORYBOOK;
}
/**
 * Whether this build should use local services for some backends
 *
 * Note we don't check hasProcessAccess() here, because these environment variables are provided by defines in the
 * frontend bundling system (inherited from CRA)
 */
export function shouldUseLocalServices(specificService = undefined) {
    if (!isDevEnv()) {
        return false;
    }
    // NOTE: don't try to refactor this to use a dynamic env var name, that doesn't work with DefinePlugin
    return (getFirebaseEnvironment() === "emulated" ||
        (specificService === "foo" && process.env.REACT_APP_USE_LOCAL_FOO === "true"));
}
export function isCypressTestUserAgent() {
    return hasBrowserWindowAccess() && !!window?.navigator?.userAgent?.match(/cypress/i);
}
/**
 * Are we testing?
 */
export function isTestEnv() {
    return areWeTestingWithJest() || isCypressTestUserAgent();
}
/**
 * @see https://stackdiary.com/tutorials/how-to-check-web-workers-browser-support-in-javascript/
 */
export function areWebWorkersSupported() {
    return (hasBrowserWindowAccess() &&
        typeof window.Worker !== "undefined" &&
        typeof window.postMessage === "function" &&
        typeof window.onmessage === "object");
}
/**
 * Names of supported environments.
 */
export const EnvironmentName = {
    // The big 3
    Production: "production",
    Release: "release",
    Main: "main",
    Preview: "preview", // deploy preview
    Branch: "branch", // branch deploy
    Development: "development", // A default deploy that doesn't set the value (NOT localhost:3000)
    Local: "local", // localhost:3000, pnpm start
};
/**
 * Please note this will only return true if a user is in prod.
 * If running in a test environment, this will NOT be true!
 * Sometimes we might want to allow operations in both prod and RF tests, then
 * this is NOT the function you want to use! For example, see the
 * `process.env.NODE_ENV === "production"` in `serviceWorkerRegistration.ts`
 *
 * @returns true if this is running in production environment (e.g. frontend on www.flux.ai, without a split-testing override, or backend on our main Firebase account)
 */
export function isProductionEnvironment() {
    return getEnvironmentName() === EnvironmentName.Production;
}
/**
 * Gets the environment name from either the build or the current hostname
 */
export function getEnvironmentName() {
    return getEnvironmentNameFromBuild() ?? getEnvironmentNameFromNodeProcessEnv();
}
/**
 * Returns the name of current environment for use in reporting.
 *
 * @returns name of the current environment (i.e. production, release, local, etc)
 */
export function getEnvironmentNameFromBuild() {
    return process.env.REACT_APP_ENVIRONMENT_NAME
        ? process.env.REACT_APP_ENVIRONMENT_NAME
        : undefined;
}
export function getEnvironmentNameFromNodeProcessEnv() {
    // Jest "doesn't count" as node - the logic in this function is mostly for use with e.g. backend
    if (!isNode() || areWeTestingWithJest()) {
        return undefined;
    }
    return process.env.FUNCTIONS_EMULATOR ? EnvironmentName.Local : EnvironmentName.Production;
}
export function createMergeRequestSlug() {
    if (process.env.REACT_APP_GITHUB_PR_ID) {
        const mergeRequestNumber = process.env.REACT_APP_GITHUB_PR_ID;
        const environmentNamePrefix = "MR-";
        return environmentNamePrefix + mergeRequestNumber;
    }
    return undefined;
}
export function getCurrentAppVersionCommitRef() {
    return process.env.REACT_APP_GITHUB_COMMIT_REF;
}
/**
 * These variables only exists on builds for PRs in Github. See frontend/.env files.
 * NOTE: This is true for all UI tests.
 */
export function isMergeRequestPreviewEnvironment() {
    return !!process.env.REACT_APP_GITHUB_PR_ID && process.env.REACT_APP_PR_PREVIEW === "true";
}
if (hasBrowserWindowAccess()) {
    set(window, "__FLUX__.env.isDevEnv", isDevEnv());
    set(window, "__FLUX__.env.environmentName", getEnvironmentName());
    set(window, "__FLUX__.env.hasProcessAccess", hasProcessAccess());
    set(window, "__FLUX__.env.shouldUseLocalServices.all", shouldUseLocalServices());
    set(window, "__FLUX__.env.shouldUseLocalServices.foo", shouldUseLocalServices("foo"));
}
//# sourceMappingURL=env.js.map